import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys, mutationKeys } from '@/lib/query-keys';
import { useToast } from '@/hooks/use-toast';

/**
 * Represents a soft-deleted contact with metadata for trash management
 */
export interface DeletedContact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  is_company: boolean;
  primary_email: string | null;
  deleted_at: string;
  deleted_by: string | null;
  deleted_by_name: string | null;
  days_until_purge: number;
}

/**
 * Result from restoring contacts
 */
export interface RestoreResult {
  restored_count: number;
  success: boolean;
  message: string;
}

/**
 * Fetches deleted contacts from the database via RPC
 */
async function fetchDeletedContacts(
  workspaceId: string,
  limit: number,
  offset: number
): Promise<DeletedContact[]> {
  if (!workspaceId) return [];

  const { data, error } = await supabase.rpc('list_deleted_contacts', {
    p_workspace_id: workspaceId,
    p_limit: limit,
    p_offset: offset,
  });

  if (error) {
    console.error('[useDeletedContacts] Error fetching deleted contacts:', error);
    throw new Error(error.message);
  }

  return (data as DeletedContact[]) ?? [];
}

/**
 * Restores soft-deleted contacts via RPC
 */
async function restoreContactsApi({
  contactIds,
  workspaceId,
}: {
  contactIds: string[];
  workspaceId: string;
}): Promise<RestoreResult> {
  if (!contactIds || contactIds.length === 0) {
    throw new Error('No contact IDs provided for restore');
  }

  if (!workspaceId) {
    throw new Error('Workspace ID is required for restore');
  }

  const { data, error } = await supabase.rpc('restore_contacts', {
    p_contact_ids: contactIds,
    p_workspace_id: workspaceId,
  });

  if (error) {
    console.error('[useDeletedContacts] Error restoring contacts:', error);
    throw new Error(error.message);
  }

  // RPC returns array with single result row
  const result = Array.isArray(data) ? data[0] : data;
  return result as RestoreResult;
}

/**
 * Hook to list deleted contacts in a workspace
 *
 * Uses TanStack Query for caching and automatic refetching.
 * Returns deleted contacts with pagination support.
 *
 * @param workspaceId - The workspace to fetch deleted contacts from
 * @param limit - Maximum number of contacts to fetch (default: 50)
 * @param offset - Number of contacts to skip for pagination (default: 0)
 */
export function useListDeletedContacts(
  workspaceId: string | undefined,
  limit = 50,
  offset = 0
) {
  return useQuery({
    queryKey: [...queryKeys.contacts.deleted(workspaceId), { limit, offset }],
    queryFn: () => fetchDeletedContacts(workspaceId!, limit, offset),
    enabled: !!workspaceId,
    staleTime: 1000 * 60, // Consider data stale after 1 minute
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  });
}

/**
 * Hook to restore deleted contacts
 *
 * Provides a mutation for restoring soft-deleted contacts.
 * On success, invalidates both deleted contacts and main contacts queries.
 *
 * @example
 * const restore = useRestoreContacts();
 * restore.mutate({ contactIds: ['id1', 'id2'], workspaceId: 'ws-id' });
 */
export function useRestoreContacts() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: mutationKeys.restoreContacts,
    mutationFn: (params: { contactIds: string[]; workspaceId: string }) =>
      restoreContactsApi(params),
    onSuccess: (result) => {
      const count = result.restored_count;
      toast({
        title: 'Contacts Restored',
        description: `Successfully restored ${count} contact${count !== 1 ? 's' : ''}.`,
      });

      // Invalidate deleted contacts list to refresh the trash view
      queryClient.invalidateQueries({
        queryKey: queryKeys.contacts.deleted(),
      });

      // Invalidate main contacts list to show restored contacts
      queryClient.invalidateQueries({
        queryKey: queryKeys.contacts.lists(),
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Restore Failed',
        description: error.message || 'Failed to restore contacts. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
